import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  type ActionCodeSettings,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";

const STORAGE_EMAIL_KEY = "dotperson:signin-email";

function buildActionCodeSettings(redirect: string | null): ActionCodeSettings {
  const url = new URL("/admin/sign-in/complete", window.location.origin);
  if (redirect) url.searchParams.set("redirect", redirect);
  return {
    url: url.toString(),
    handleCodeInApp: true,
  };
}

export function SignIn() {
  const [params] = useSearchParams();
  const redirect = params.get("redirect");
  const [email, setEmail] = React.useState("");
  const [status, setStatus] = React.useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = React.useState<string>("");

  // If the user lands here while ALREADY holding a magic-link in this URL,
  // bounce them to the complete page.
  React.useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const completeUrl = new URL("/admin/sign-in/complete", window.location.origin);
      window.location.href = completeUrl.toString();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      await sendSignInLinkToEmail(auth, trimmed, buildActionCodeSettings(redirect));
      window.localStorage.setItem(STORAGE_EMAIL_KEY, trimmed);
      setStatus("sent");
    } catch (err) {
      setErrorMsg(`Could not send sign-in link: ${String(err)}`);
      setStatus("error");
    }
  };

  return (
    <PageShell title="Admin · Sign in" status="restricted">
      <Seo
        title="Sign in — The .person Foundation admin"
        description="Foundation admin sign-in via magic link."
        path="/admin/sign-in"
        noindex
      />
      <div className="max-w-md mx-auto py-12">
        {status === "sent" ? (
          <div className="space-y-6">
            <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
              Link sent
            </p>
            <p className="font-serif text-base text-ink leading-relaxed">
              Check your inbox at <strong>{email}</strong>. Click the sign-in link
              to complete authentication. The link is valid for 1 hour and works
              once.
            </p>
            <button
              type="button"
              onClick={() => setStatus("idle")}
              className="font-mono text-[10px] text-ink-3 underline hover:text-ink"
            >
              Send to a different address
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <p className="font-serif text-base text-ink-2 leading-relaxed">
              Enter the email address associated with your Foundation contributor
              record. We&apos;ll email a one-time sign-in link.
            </p>
            <div>
              <label
                htmlFor="signin-email"
                className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-2"
              >
                Email
              </label>
              <input
                id="signin-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-paper border border-ink-3/30 px-4 py-3 font-mono text-sm text-ink focus:outline-none focus:border-ink"
                placeholder="you@example.com"
                required
              />
            </div>
            {errorMsg && (
              <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500">
                {errorMsg}
              </div>
            )}
            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full bg-ink text-paper font-mono text-xs uppercase tracking-widest py-3 disabled:opacity-50"
            >
              {status === "sending" ? "Sending…" : "Email me a sign-in link"}
            </button>
          </form>
        )}
        <p className="mt-12 font-mono text-[10px] text-ink-3">
          <Link to="/" className="underline">
            ← back to dotperson.org
          </Link>
        </p>
      </div>
    </PageShell>
  );
}
